import GameObject, { IGameObjectCreateParams } from '../../../GameObject/GameObject';
import CardComponent from '../../../Component/Level/Card';
import Sprite from '../../Sprite';
import Point from '../../../Utility/Point';
import TransformComponent from '../../../Component/Transform';
import Interactable from '../../../Component/Interactable';
import AnimationComponent from '../../../Component/Animation';
import Renderable from '../../../Component/Renderable';
import Armature from '../../Armature';
import CardModifier from './Modifier/CardModifier';
import { Constructor } from 'Utility/Types';
import { ICardModifierData } from './ICardModifierData';
import Container from 'Prototype/Container';
import { ModifierTypeMap, getCardModifierHandler } from './ModifierTypeMap';
import { IBaseCardData } from 'State/Card/ICardState';

export interface IBaseCardCreateParams extends IGameObjectCreateParams, Partial<IBaseCardData> {
    modifiers?: ICardModifierData[];
    faceUp: boolean;
    cardFrontTexture?: string;
    cardBackTexture?: string;
    dragonbonesName?: string;
    armatureName?: string;
}

const ARMATURE_NAME = 'cardArmature';

export enum BaseCardEvent {
    FLIP_START = 'flipStart',
    INTRO_FALL_IN_COMPLETE = 'introFallInComplete',
}

export default class BaseCard extends GameObject {
    public static CARD_FRONT_NAME: string = 'cardFrontSprite';
    public static CARD_BACK_NAME: string = 'cardBackSprite';

    public score: number = 0;

    private modifierTypeMap: Map<Constructor<CardModifier>, CardModifier> = new Map<Constructor<CardModifier>, CardModifier>();
    private _modifierContainer: Container;

    protected onCreate(params: IBaseCardCreateParams): void {
        this.addComponent<TransformComponent>(TransformComponent, params);

        this.addComponent<Renderable>(Renderable, params);

        this.addComponent<CardComponent>(CardComponent, params);

        this.addComponent<Interactable>(Interactable, { buttonMode: true });

        this.addComponent<AnimationComponent>(AnimationComponent, {
            playOnStart: false,
        });

        const cardBackSprite = GameObject.Instantiate(Sprite, {
            visible: !params.faceUp,
            name: BaseCard.CARD_BACK_NAME,
            texture: params.cardBackTexture || 'backcard_1',
            anchor: new Point(0.5, 0.5),
        });

        const cardFrontSprite = GameObject.Instantiate(Sprite, {
            visible: params.faceUp,
            name: BaseCard.CARD_FRONT_NAME,
            texture: params.cardFrontTexture || 'backcard_1',
            anchor: new Point(0.5, 0.5),
        });

        this.addChild(cardBackSprite);

        this.addChild(cardFrontSprite);

        if (params.dragonbonesName) {
            const cardArmature = GameObject.Instantiate(Armature, {
                name: ARMATURE_NAME,
                dragonbonesName: params.dragonbonesName,
                armatureName: params.armatureName,
                alpha: params.forceHideArmature ?? params.faceUp ? 0 : 1,
                playOnStart: false,
            });

            this.addChild(cardArmature);
        }

        if (params.modifiers) {
            this.initializeModifiers(params.modifiers.map(modifierData => {
                return { ...modifierData, cardID: params.id };
            }));
            this.updateModifiersVisibility(params.faceUp);
        }
    }

    public getArmature(): Armature | undefined {
        const armature = this.find(ARMATURE_NAME);
        return (armature && armature instanceof Armature) ? armature as Armature : undefined;
    }

    public get modifiers(): CardModifier[] {
        return [...this.modifierTypeMap.values()].filter(modifier => !modifier.destroyed);
    }

    public updateModifiersVisibility(faceUp: boolean): void {
        this.modifiers.forEach(modifier => getCardModifierHandler(modifier).setModifierFaceUp(faceUp));
    }

    public getModifier<T extends CardModifier>(ctor: Constructor<T>): T | undefined {
        const modifier = this.modifierTypeMap.get(ctor);
        return modifier ? modifier as T : undefined;
    }

    // FIXME: There is nothing here to handle cases where multiple of the same modifier is added to a single card (this shouldn't happen but maybe in future?)
    protected addModifier(modifierParams: ICardModifierData): void {
        const ctor = ModifierTypeMap[modifierParams.type].prototype;

        if (this.modifierTypeMap.get(ctor)) return;

        const modifier = GameObject.Instantiate(
            ctor,
            {
                ...modifierParams,
                name: modifierParams.type.toString(), // Set gameObject name to type for later lookup. (Not needed anymore with the modifierTypeMap, but still nice to have?)
            }
        )
        this._modifierContainer.addChild(modifier);
        this.modifierTypeMap.set(ctor, modifier);
    }

    private initializeModifiers(modifiers: ICardModifierData[]): void {
        // Create modifier container.
        this._modifierContainer = GameObject.Instantiate(Container, {
            name: 'modifierContainer',
        });

        // Add any modifiers.
        modifiers &&
            modifiers.forEach((modifierParams: ICardModifierData) => {
                this.addModifier(modifierParams);
            });

        this.addChild(this._modifierContainer);
    }

    public hideAndDestroyModifiers(): void {
        this.modifiers.forEach((modifier) => {
            modifier.hideAndDestroy();
        });
    }

    public static async preload(): Promise<void> {}
}
